import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { TEMAS, MODOS, lerPreferencias, salvarPreferencias } from "../../utils/tema";

const CORES = { rosa: "#e83c6e", azul: "#2563eb", grafite: "#334155" };

const iniciais = nome =>
  (nome || "U").split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join("");

// Menu "Usuário" do topo: preferências do usuário e seletor rápido de tema/modo.
const MenuUsuario = ({ usuario }) => {
  const [aberto, setAberto] = useState(false);
  const [prefs, setPrefs] = useState(lerPreferencias());
  const raiz = useRef(null);
  const temaAtual = TEMAS.find(t => t.chave === prefs.tema);

  useEffect(() => {
    const atualizar = e => setPrefs(e.detail);
    window.addEventListener("va-tema-alterado", atualizar);
    return () => window.removeEventListener("va-tema-alterado", atualizar);
  }, []);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = e => raiz.current && !raiz.current.contains(e.target) && setAberto(false);
    const esc = e => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  const alterar = parcial => {
    const novas = { ...prefs, ...parcial };
    setPrefs(novas);
    salvarPreferencias(novas);
  };

  return (
    <div className="va-menu-usuario" ref={raiz}>
      <button
        type="button"
        className={`va-menu-usuario-botao ${aberto ? "aberto" : ""}`}
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto(v => !v)}
      >
        <span className="va-menu-usuario-avatar">{iniciais(usuario && usuario.nome)}</span>
        Usuário
        <Icon name="chevron down" className="va-menu-usuario-seta" />
      </button>

      {aberto && (
        <div className="va-menu-usuario-painel" role="menu">
          <div className="va-menu-usuario-cabecalho">
            <span className="va-menu-usuario-avatar grande">{iniciais(usuario && usuario.nome)}</span>
            <div>
              <b>{usuario && usuario.nome}</b>
              <small>{usuario && usuario.email}</small>
            </div>
          </div>

          <Link to="/perfil" className="va-menu-usuario-item" role="menuitem" onClick={() => setAberto(false)}>
            <Icon name="user outline" /> Preferências do usuário
          </Link>

          <div className="va-menu-usuario-secao">
            <span className="va-menu-usuario-titulo"><Icon name="paint brush" /> Temas</span>
            <div className="va-menu-usuario-temas" role="radiogroup" aria-label="Tema">
              {TEMAS.map(t => (
                <button
                  type="button"
                  key={t.chave}
                  role="radio"
                  aria-checked={prefs.tema === t.chave}
                  className={prefs.tema === t.chave ? "ativo" : ""}
                  onClick={() => alterar({ tema: t.chave })}
                  title={t.descricao}
                >
                  <i style={{ background: CORES[t.chave] }} />
                  {t.nome}
                </button>
              ))}
            </div>

            <span className="va-menu-usuario-titulo"><Icon name="adjust" /> Modo</span>
            <div className="va-modo-grupo compacto" role="radiogroup" aria-label="Modo">
              {MODOS.map(m => (
                <button
                  type="button"
                  key={m.chave}
                  role="radio"
                  aria-checked={prefs.modo === m.chave}
                  className={prefs.modo === m.chave ? "ativo" : ""}
                  disabled={!temaAtual.escuro}
                  onClick={() => alterar({ modo: m.chave })}
                >
                  {m.nome}
                </button>
              ))}
            </div>
            {!temaAtual.escuro && <small className="va-menu-usuario-nota">O tema Grafite não tem modo escuro.</small>}
          </div>

          <Link to="/logout" className="va-menu-usuario-item sair" role="menuitem" onClick={() => setAberto(false)}>
            <Icon name="sign-out" /> Sair
          </Link>
        </div>
      )}
    </div>
  );
};

MenuUsuario.propTypes = {
  usuario: PropTypes.shape({ nome: PropTypes.string, email: PropTypes.string })
};

export default MenuUsuario;
